/**
 * TailoringBatchCard — interactive sign-off card for `propose_tailoring_batch` (PLT-4895), the
 * "own insights" card.
 *
 * The chat-only tool `propose_tailoring_batch` validates a structured proposal payload (one or more
 * CSRD result tailorings the agent thinks belong on the company's tailored version) and echoes it
 * back as the tool result. This card renders that payload as a grouped checklist:
 *
 *   - Requested in this chat — changes the user asked for directly, no file behind them, pre-checked
 *                              (PLT-7539).
 *   - Strong evidence — file-backed, well-supported items, pre-checked.
 *   - Limited or ambiguous evidence — items raised but not quantified, unchecked by default.
 *   - Carried over — items the user ratified on a prior DMA period (`list_importable_tailorings`).
 *
 * PLT-7899: each row is headed by the impact, risk or opportunity it changes, read with its current
 * values and its projected outcome from `ownInsightContext`, which the server builds from the tailored
 * version (`own-insight-proposal-rows.ts`). A proposal made before that existed has no context, and its
 * rows fall back to the model's `summary`.
 *
 * The user (de)selects rows, optionally opens a row's details to edit it, then clicks "Integrate N own
 * insights". That click does NOT commit: it opens a confirmation step that says what integrating does,
 * because the write edits the company's tailored DMA. Confirming POSTs directly to
 * `POST /api/materiality/{companyId}/result-tailoring-batch/{variantId}` — NOT back through the LLM —
 * so the commit is deterministic and audit-clean. After a successful apply, the card becomes a receipt
 * that follows the recalculation, then names the matters whose materiality changed, read from the
 * recalculated results. The receipt counts insights, not the underlying tailoring rows, so its count
 * equals the number of rows (PLT-5058).
 *
 * "Own insights" / "Integrate" is the platform's wording for the feature (dma-v2-view dashboard:
 * "Integrate your own insights … for a single, unified version"). Internal code and the API still say
 * "tailoring" and "apply".
 *
 * Restricted-company handling: the data the proposal references was already pulled through guarded
 * data tools, and the actual write is gated server-side by `EDIT_CSRD_TAILORINGS` on the tailored
 * variant. The card reads only the tailored version's own results after the recalculation, which the
 * same user can read.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAssistantRuntime, useThread } from '@assistant-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  OwnInsightFieldKey,
  OwnInsightMatter,
  OwnInsightNotProjectedReason,
  OwnInsightProposalContext,
  OwnInsightRow as OwnInsightRowData,
} from '@upright/common/mcp-payloads/own-insight-proposal';
import {
  CompanyLevelMaterialityResult,
  FinancialDriver,
  FinancialItem,
  FinancialMaterialityLevel,
  GenericLevel,
  MaterialityActualness,
  MaterialityTimeHorizon,
  MaterialityValence,
  MaterialityValueChain,
  TimeHorizonWithoutUnknown,
} from '@upright/common/model/input/materiality-assessment/types';
import type { CsrdResultTailoring } from '@upright/common/model/input/materiality-assessment/types-tailoring';
import { levelToNumber } from '@upright/common/model/input/materiality-assessment/utils';
import {
  MAX_TAILORING_BATCH_ITEMS,
  type BatchApplyCsrdResultTailoringResponse,
} from '@upright/common/model/output/materiality/csrd-company-results/result-tailoring/api-types';
import { driverToValidFinancialItems } from '@upright/common/model/output/materiality/csrd-company-results/result-tailoring/utils';
import { friendlyMaterialityTimeHorizon } from '@upright/common/model/output/materiality/friendly-materiality-time-horizon';
import {
  friendlyFinancialMaterialityLevel,
  friendlyGenericLevel,
  friendlyMaterialityActualnesses,
  friendlyValueChain,
} from '@upright/common/model/output/materiality/utils';
import { JobStatus } from '@upright/common/internal/jobs/types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { getIsJobComplete, useFetchJobStatus } from '@upright/ui/api/internal/jobs/use-fetch-job-status';
import { fetchMaterialityForVariant } from '@upright/ui/api/model/output/scores/materiality/company';
import { Badge } from '@upright/ui/common/generic/components/badge';
import { Button, LinkButton } from '@upright/ui/common/generic/components/button';
import { DataCard } from '@upright/ui/common/generic/components/card';
import { Option, Select } from '@upright/ui/common/generic/components/dropdowns';
import { Input, TextArea } from '@upright/ui/common/generic/components/input';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Link } from '@upright/ui/common/generic/components/link';
import { Spinner } from '@upright/ui/common/generic/components/spinner';
import { Heading, HeadingGroup, Paragraph, Text } from '@upright/ui/common/generic/components/typography';
import { ChatMarkdown } from './upright-chat-markdown';
import {
  ChangePart,
  DetailsToggle,
  OwnInsightRow,
  OwnInsightRows,
  matterLine,
  ownInsightHeadline,
} from './own-insight-row';
import { sameOriginPlatformPath } from './upright-platform-link';
import { FOLLOW_THREAD_EVENT } from './upright-chat-ui-common';
import styles from './own-insight-cards.module.css';

// ---------------------------------------------------------------------------
// Types — the shape the chat-only tool echoes back. Kept inline rather than
// re-imported from the backend module so the UI bundle does not pull in zod /
// the server-only zCsrdTailoringSchema. Structurally matches
// `ProposeTailoringBatchInput` in `mcp/tools/propose-tailoring-batch.ts`.
// ---------------------------------------------------------------------------

type Group = 'user-request' | 'strong-evidence' | 'gaps' | 'prior-dma';
type Source = 'file' | 'prior-dma' | 'user-request';
type Confidence = 'high' | 'medium' | 'low';
type ConflictRecommendation = 'prefer-file' | 'prefer-prior-dma' | 'hybrid';

interface ProposedItem {
  itemId: string;
  group: Group;
  source: Source;
  preChecked: boolean;
  summary: string;
  matterId?: string;
  matterName?: string;
  // PLT-4960: when set on a `patch-*` item, applying it supersedes (atomically disables) this
  // existing Upright IRO and creates the edited copy. Passed straight to the apply endpoint.
  existingIro?: {
    clientId: string;
    description: string;
    impactTopicId?: string;
  };
  tailoringType: string;
  tailoring: CsrdResultTailoring;
  reasoning: string;
  // PLT-5066: for `group: 'gaps'` items, why the evidence is limited or ambiguous.
  evidenceGapNote?: string;
  citationId?: string;
  confidence: Confidence;
  conflict?: {
    withItemId?: string;
    note: string;
    recommendation: ConflictRecommendation;
  };
}

interface ProposalCitation {
  id: string;
  label: string;
}

export interface TailoringBatchCardProps {
  companyId: string;
  /**
   * The company's variant (original or tailored). The backend resolves it to the tailored variant
   * via `getVariantTailoringLink`. When none resolves, the card disables Integrate and says why.
   */
  variantId: string | null;
  proposalId: string;
  summaryMarkdown?: string;
  priorDmaLabel?: string;
  items: ProposedItem[];
  citations?: ProposalCitation[];
  /** PLT-7899: absent on a proposal made before the server built it. */
  ownInsightContext?: OwnInsightProposalContext;
}

// Editable → confirming → applying → applied, with discarded and failed beside them. Selection and
// edits are locked from the confirmation step on, so what the user confirms is what is written. A
// failure keeps the proposal editable, because nothing was created.
type ApplyState =
  | { phase: 'idle' }
  | { phase: 'confirming' }
  | { phase: 'applying' }
  | { phase: 'applied'; result: BatchApplyCsrdResultTailoringResponse }
  | { phase: 'discarded' }
  | { phase: 'failed'; message: string };

const GROUP_ORDER: Group[] = ['user-request', 'strong-evidence', 'gaps', 'prior-dma'];

const GROUP_LABELS: Record<Group, string> = {
  'user-request': 'Requested in this chat',
  'strong-evidence': 'Strong evidence',
  // PLT-4968 (Roni): the group key stays `gaps`, but "Gaps" read as a value judgement on the company.
  gaps: 'Limited or ambiguous evidence',
  'prior-dma': 'Carried over from previous DMA',
};

const GROUP_HINTS: Partial<Record<Group, string>> = {
  gaps: 'Raised but not quantified. Review each one before you include it.',
};

/** Read after "Source:" when the item cites nothing more specific. */
const SOURCE_LABELS: Record<Source, string> = {
  file: 'uploaded file',
  'prior-dma': 'previous DMA',
  'user-request': 'your request',
};

// PLT-4968 (Roni): the high/medium/low confidence is not shown per row: it read as a verdict on the
// evidence, and the grouping already conveys evidence strength.

const RECOMMENDATION_LABEL: Record<ConflictRecommendation, string> = {
  'prefer-file': 'Prefer the file',
  'prefer-prior-dma': 'Prefer the previous DMA',
  hybrid: 'A mix of both is recommended',
};

/** The action word for a row without server context: the same words the server builds. */
const FALLBACK_ACTION_LABEL: Record<string, string> = {
  'patch-im': 'Edit impact',
  'remove-im': 'Disable impact',
  'remove-fm': 'Disable risk or opportunity',
  'threshold-im': 'Change impact threshold',
  'threshold-fm': 'Change financial threshold',
  'threshold-fe': 'Change financial-effects threshold',
};

// ---------------------------------------------------------------------------
// Editable fields. The agent proposes a tailoring; the user can correct it before integrating. The
// controls are driven by the same enum option lists the platform's single-tailoring forms use, so an
// edited item stays schema-valid by construction. Impacts (`patch-im`) and risks/opportunities
// (`patch-fm`, `patch-fe`) are editable; a disable or a threshold carries only its reasoning.
// ---------------------------------------------------------------------------

type EditValue = string | number | undefined;
type ItemEdits = Record<string, EditValue>;
type SelectOption = { value: string; label: string };

/** "Medium 3": a rating level carries its number (DESIGN.md, "Numbers, units and dates"). */
function genericLevelLabel(level: GenericLevel): string {
  return `${friendlyGenericLevel(level)} ${levelToNumber(level)}`;
}

const GENERIC_LEVEL_OPTIONS: SelectOption[] = Object.values(GenericLevel).map(level => ({
  value: level,
  label: genericLevelLabel(level),
}));

const TIME_HORIZON_OPTIONS: SelectOption[] = TimeHorizonWithoutUnknown.map(horizon => ({
  value: horizon,
  label: friendlyMaterialityTimeHorizon(horizon),
}));

const FINANCIAL_LEVEL_OPTIONS: SelectOption[] = Object.values(FinancialMaterialityLevel).map(level => ({
  value: level,
  label: friendlyFinancialMaterialityLevel(level),
}));

// FinancialDriver / FinancialItem values are already human-readable ("Investors", "CapEx").
const FINANCIAL_DRIVER_OPTIONS: SelectOption[] = Object.values(FinancialDriver).map(driver => ({
  value: driver,
  label: driver,
}));

type FieldDef = {
  key: OwnInsightFieldKey;
  label: string;
  options: SelectOption[];
  /** Irremediability applies to negative impacts only. */
  negativeOnly?: true;
};

const IMPACT_FIELDS: FieldDef[] = [
  { key: 'scaleLevel', label: 'Scale', options: GENERIC_LEVEL_OPTIONS },
  { key: 'scopeLevel', label: 'Scope', options: GENERIC_LEVEL_OPTIONS },
  { key: 'probabilityLevel', label: 'Likelihood', options: GENERIC_LEVEL_OPTIONS },
  { key: 'irremediabilityLevel', label: 'Irremediability', options: GENERIC_LEVEL_OPTIONS, negativeOnly: true },
  { key: 'timeHorizon', label: 'Time horizon', options: TIME_HORIZON_OPTIONS },
  {
    key: 'valueChain',
    label: 'Value chain',
    options: Object.values(MaterialityValueChain).map(chain => ({ value: chain, label: friendlyValueChain(chain) })),
  },
  {
    key: 'actualness',
    label: 'Actualness',
    options: Object.values(MaterialityActualness).map(actualness => ({
      value: actualness,
      label: friendlyMaterialityActualnesses([actualness]),
    })),
  },
];

function isEditableTailoring(item: ProposedItem): boolean {
  return item.tailoringType === 'patch-im' || item.tailoringType === 'patch-fm' || item.tailoringType === 'patch-fe';
}

/**
 * A patch without an anchor adds an impact, risk or opportunity. A missing `before` does not say
 * that: an edit's IRO can also be missing from the results the row was built from.
 */
function isNewIro(item: ProposedItem): boolean {
  return isEditableTailoring(item) && !item.existingIro;
}

function isThresholdTailoring(item: ProposedItem): boolean {
  return item.tailoringType.startsWith('threshold-');
}

/** The fields whose values a row compares: the impact's, or the risk's or opportunity's levels. */
function comparedFields(item: ProposedItem): FieldDef[] {
  if (item.tailoringType === 'patch-im') {
    const isNegative = (item.tailoring as { valence?: MaterialityValence }).valence === MaterialityValence.NEGATIVE;
    return IMPACT_FIELDS.filter(field => !field.negativeOnly || isNegative);
  }
  if (item.tailoringType === 'patch-fm') {
    return [
      { key: 'probability', label: 'Probability', options: FINANCIAL_LEVEL_OPTIONS },
      { key: 'magnitude', label: 'Magnitude', options: FINANCIAL_LEVEL_OPTIONS },
      { key: 'timeHorizon', label: 'Time horizon', options: TIME_HORIZON_OPTIONS },
    ];
  }
  return [];
}

/**
 * The field's options, plus the current value when it is not one of them (a proposal that keeps
 * Upright's unknown time horizon), so the control shows that value rather than an empty "Select…".
 */
function optionsWithCurrent(field: FieldDef, value: string): SelectOption[] {
  if (value === '' || field.options.some(option => option.value === value)) return field.options;
  return [...field.options, { value, label: fieldValueLabel(field, value) ?? value }];
}

/** A field value's label. Upright's own value can be one the user cannot pick, such as an unknown time horizon. */
function fieldValueLabel(field: FieldDef, value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (field.key === 'timeHorizon') return friendlyMaterialityTimeHorizon(value as MaterialityTimeHorizon);
  return field.options.find(option => option.value === value)?.label ?? value;
}

// ratio (0–1) <-> percentage (0–100) for the financial-effect value fields, as the platform's
// PercentageInput does.
function ratioToPercentInput(value: EditValue): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return String(Math.round(value * 10000) / 100);
}
function percentInputToRatio(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed / 100;
}

/** The tailoring that will be submitted: the proposed one with the user's edits merged in. */
function effectiveTailoring(item: ProposedItem, edits: ItemEdits | undefined): CsrdResultTailoring {
  if (!edits || Object.keys(edits).length === 0) return item.tailoring;
  const merged = { ...item.tailoring, ...edits };
  return merged as CsrdResultTailoring;
}

/** True when the user changed a field that the projected score depends on. */
function hasEditedScoredField(edits: ItemEdits | undefined): boolean {
  if (!edits) return false;
  return Object.keys(edits).some(key => key !== 'description' && key !== 'reasoning');
}

function insightsCountLabel(count: number): string {
  return count === 1 ? '1 own insight' : `${count} own insights`;
}

function joinSubtitle(items: Array<string | undefined>): string {
  return items.filter((item): item is string => item !== undefined && item !== '').join(' · ');
}

// ---------------------------------------------------------------------------
// Top-level component.
// ---------------------------------------------------------------------------

export function TailoringBatchCard({
  companyId,
  variantId,
  proposalId,
  summaryMarkdown,
  priorDmaLabel,
  items,
  citations,
  ownInsightContext,
}: TailoringBatchCardProps) {
  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const item of items) initial[item.itemId] = item.preChecked;
    return initial;
  });
  const [applyState, setApplyState] = useState<ApplyState>({ phase: 'idle' });
  // PLT-4959: per-item field edits, a sparse map itemId → { field → value }.
  const [edits, setEdits] = useState<Record<string, ItemEdits>>({});

  const rowsById = new Map((ownInsightContext?.rows ?? []).map(row => [row.itemId, row]));
  const grouped = groupItems(items);
  const selectedItems = items.filter(item => selected[item.itemId]);
  const selectedCount = selectedItems.length;
  const isConfirming = applyState.phase === 'confirming';
  const isApplying = applyState.phase === 'applying';
  const locked = isConfirming || isApplying;

  const toggle = (itemId: string, value: boolean) => {
    if (locked) return;
    setSelected(prev => ({ ...prev, [itemId]: value }));
  };

  const setAllInGroup = (group: Group, value: boolean) => {
    if (locked) return;
    setSelected(prev => {
      const next = { ...prev };
      for (const item of items) if (item.group === group) next[item.itemId] = value;
      return next;
    });
  };

  const editField = (itemId: string, field: string, value: EditValue) => {
    if (locked) return;
    setEdits(prev => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));
  };

  const queryClient = useQueryClient();

  const apply = async () => {
    if (selectedCount === 0 || !isConfirming || variantId === null) return;
    setApplyState({ phase: 'applying' });
    try {
      const result = await fetchOrThrow<BatchApplyCsrdResultTailoringResponse>(
        `/materiality/${encodeURIComponent(companyId)}/result-tailoring-batch/${encodeURIComponent(variantId)}`,
        'POST',
        {
          proposalId,
          // PLT-4960: the supersede anchor is acted on only for `patch-*` items.
          items: selectedItems.map(item => ({
            itemId: item.itemId,
            tailoring: effectiveTailoring(item, edits[item.itemId]),
            ...(item.existingIro ? { existingIro: item.existingIro } : {}),
          })),
        },
      );
      setApplyState({ phase: 'applied', result });
      // The tailoring rows are committed now; the recompute runs async (see the receipt).
      void queryClient.invalidateQueries({ queryKey: ['materiality'] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The own insights could not be integrated.';
      setApplyState({ phase: 'failed', message });
    }
  };

  if (applyState.phase === 'applied') {
    // Only the submitted items: the receipt derives "integrated" as submitted minus skipped and
    // refused, so the full list would show unselected items as integrated (PLT-4895).
    return (
      <TailoringBatchReceipt
        result={applyState.result}
        items={selectedItems}
        rowsById={rowsById}
        context={ownInsightContext}
        priorDmaLabel={priorDmaLabel}
      />
    );
  }

  // Discarding keeps the selection and edits, so the proposal can be reopened as it was left.
  if (applyState.phase === 'discarded') {
    return (
      <DataCard>
        <Stack gap="lg">
          <HeadingGroup>
            <Heading level={3} size="lg" height="lg" weight="bold" color="gray-900">
              Proposal discarded
            </Heading>
            <Paragraph size="sm" height="xs" color="gray-600">
              {joinSubtitle([ownInsightContext?.companyName, `${items.length} proposed`, 'nothing was integrated'])}
            </Paragraph>
          </HeadingGroup>
          <Stack row gap="sm" align="center" wrap>
            <Button
              variant="outlined"
              size="small"
              onPress={() => {
                setApplyState({ phase: 'idle' });
              }}
            >
              Undo discard
            </Button>
          </Stack>
        </Stack>
      </DataCard>
    );
  }

  const companyName = ownInsightContext?.companyName;

  return (
    <DataCard>
      <Stack gap="lg">
        <HeadingGroup>
          <Heading level={3} size="lg" height="lg" weight="bold" color="gray-900">
            Own insights to integrate
          </Heading>
          <Paragraph size="sm" height="xs" color="gray-600">
            {joinSubtitle([
              companyName,
              priorDmaLabel ? `Carried over from ${priorDmaLabel}` : undefined,
              `${selectedCount} of ${items.length} selected`,
            ])}
          </Paragraph>
        </HeadingGroup>
        {summaryMarkdown && summaryMarkdown.length > 0 && <ChatMarkdown>{summaryMarkdown}</ChatMarkdown>}
        {GROUP_ORDER.map(group => {
          const itemsOfGroup = grouped[group];
          if (itemsOfGroup.length === 0) return null;
          return (
            <ProposalGroup
              key={group}
              group={group}
              items={itemsOfGroup}
              selected={selected}
              edits={edits}
              rowsById={rowsById}
              citations={citations}
              priorDmaLabel={priorDmaLabel}
              locked={locked}
              onToggle={toggle}
              onEditField={editField}
              onSetAll={value => {
                setAllInGroup(group, value);
              }}
            />
          );
        })}
        {isConfirming ? (
          <CommitStep
            selectedItems={selectedItems}
            rowsById={rowsById}
            companyName={companyName}
            edits={edits}
            onBack={() => {
              setApplyState({ phase: 'idle' });
            }}
            onConfirm={() => {
              void apply();
            }}
          />
        ) : (
          // Secondary action first, primary last (DESIGN.md, "Where the action row goes").
          <Stack row gap="sm" align="center" wrap>
            <Button
              variant="outlined"
              size="small"
              isDisabled={isApplying}
              onPress={() => {
                setApplyState({ phase: 'discarded' });
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              size="small"
              isDisabled={variantId === null || selectedCount === 0 || isApplying}
              onPress={() => {
                setApplyState({ phase: 'confirming' });
              }}
            >
              {isApplying
                ? 'Integrating…'
                : selectedCount === 0
                  ? 'Integrate'
                  : `Integrate ${insightsCountLabel(selectedCount)}`}
            </Button>
          </Stack>
        )}
        {applyState.phase === 'failed' && (
          <div role="alert">
            <Text size="sm" height="xs" color="gray-800">
              {applyState.message}
            </Text>
          </div>
        )}
        {variantId === null && (
          <Text size="sm" height="xs" color="gray-600">
            Own insights can be integrated only for a company whose tailored version you can edit.
          </Text>
        )}
        {items.length >= MAX_TAILORING_BATCH_ITEMS && (
          <Text size="sm" height="xs" color="gray-600">
            This proposal holds the most one card can, {MAX_TAILORING_BATCH_ITEMS}. Ask for the rest in a follow-up
            proposal.
          </Text>
        )}
      </Stack>
    </DataCard>
  );
}

// ---------------------------------------------------------------------------
// Groups and rows.
// ---------------------------------------------------------------------------

function ProposalGroup({
  group,
  items,
  selected,
  edits,
  rowsById,
  citations,
  priorDmaLabel,
  locked,
  onToggle,
  onEditField,
  onSetAll,
}: {
  group: Group;
  items: ProposedItem[];
  selected: Record<string, boolean>;
  edits: Record<string, ItemEdits>;
  rowsById: Map<string, OwnInsightRowData>;
  citations: ProposalCitation[] | undefined;
  priorDmaLabel: string | undefined;
  locked: boolean;
  onToggle: (itemId: string, value: boolean) => void;
  onEditField: (itemId: string, field: string, value: EditValue) => void;
  onSetAll: (value: boolean) => void;
}) {
  const checkedInGroup = items.filter(item => selected[item.itemId]).length;
  const label =
    group === 'prior-dma' && priorDmaLabel ? `${GROUP_LABELS[group]} (${priorDmaLabel})` : GROUP_LABELS[group];
  const hint = GROUP_HINTS[group];
  return (
    <Stack gap="sm">
      <div className={styles.groupHead}>
        <Text size="sm" height="xs" weight="semibold" color="gray-900">
          {label}
        </Text>
        <Text size="sm" height="xs" color="gray-600">
          {checkedInGroup} of {items.length}
        </Text>
        {!locked && items.length > 1 && (
          <span className={styles.groupActions}>
            <Text size="sm" height="xs">
              <Link
                onPress={() => {
                  onSetAll(true);
                }}
              >
                Select all
              </Link>
            </Text>
            <Text size="sm" height="xs">
              <Link
                onPress={() => {
                  onSetAll(false);
                }}
              >
                Clear
              </Link>
            </Text>
          </span>
        )}
      </div>
      {hint && (
        <Text size="sm" height="xs" color="gray-600">
          {hint}
        </Text>
      )}
      <OwnInsightRows>
        {items.map(item => (
          <ProposalRow
            key={item.itemId}
            item={item}
            row={rowsById.get(item.itemId)}
            isSelected={!!selected[item.itemId]}
            edits={edits[item.itemId]}
            citationLabel={citations?.find(citation => citation.id === item.citationId)?.label}
            locked={locked}
            onToggle={value => {
              onToggle(item.itemId, value);
            }}
            onEditField={onEditField}
          />
        ))}
      </OwnInsightRows>
    </Stack>
  );
}

function rowHeadline(item: ProposedItem, row: OwnInsightRowData | undefined): string {
  if (row) return ownInsightHeadline(row, item.summary);
  const iroName = item.existingIro?.description;
  const action = FALLBACK_ACTION_LABEL[item.tailoringType];
  return action && iroName ? `${action}: ${iroName}` : item.summary;
}

function ProposalRow({
  item,
  row,
  isSelected,
  edits,
  citationLabel,
  locked,
  onToggle,
  onEditField,
}: {
  item: ProposedItem;
  row: OwnInsightRowData | undefined;
  isSelected: boolean;
  edits: ItemEdits | undefined;
  citationLabel: string | undefined;
  locked: boolean;
  onToggle: (value: boolean) => void;
  onEditField: (itemId: string, field: string, value: EditValue) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isNew = row !== undefined && isNewIro(item);
  return (
    <OwnInsightRow
      headline={rowHeadline(item, row)}
      meta={matterLine(row?.matterName ?? item.matterName, isNew ? 'New' : undefined)}
      change={
        <>
          <ChangeLine item={item} row={row} edits={edits} />
          {item.conflict && <Badge>Conflict</Badge>}
        </>
      }
      source={citationLabel ?? SOURCE_LABELS[item.source]}
      selection={{ isSelected, isDisabled: locked, onChange: onToggle }}
      action={
        <DetailsToggle
          isOpen={isOpen}
          onToggle={() => {
            setIsOpen(open => !open);
          }}
        />
      }
      details={
        isOpen ? (
          <RowDetails item={item} row={row} edits={edits} locked={locked} onEditField={onEditField} />
        ) : undefined
      }
    />
  );
}

/**
 * What the change does to the IRO, in one line: the fields it changes, its score before and after,
 * and whether it then reaches the materiality threshold. Once the user edits a scored field the
 * projection no longer holds, so the line says the score is recalculated on integrating.
 */
function ChangeLine({
  item,
  row,
  edits,
}: {
  item: ProposedItem;
  row: OwnInsightRowData | undefined;
  edits: ItemEdits | undefined;
}): ReactNode {
  if (!row) return undefined;
  const { before, after } = row;
  const parts: ReactNode[] = [];

  if (after.kind === 'threshold') {
    const unit = after.unit === 'percent' ? '%' : '';
    const from = after.previousThreshold === undefined ? '' : `${formatNumber(after.previousThreshold)}${unit} → `;
    return <ChangePart>{`Threshold ${from}${formatNumber(after.threshold)}${unit}`}</ChangePart>;
  }

  const changedFields = comparedFields(item).filter(field => {
    const proposed = proposedValue(item, edits, field.key);
    return before !== undefined && proposed !== undefined && before.fields[field.key] !== proposed;
  });
  for (const field of changedFields.slice(0, 2)) {
    const from = fieldValueLabel(field, before?.fields[field.key]);
    const to = fieldValueLabel(field, proposedValue(item, edits, field.key));
    parts.push(<ChangePart key={field.key}>{`${field.label} ${from ?? '—'} → ${to ?? '—'}`}</ChangePart>);
  }

  if (after.kind === 'removed') {
    parts.push(
      <ChangePart key="score">
        {before?.score !== undefined ? `Score ${formatScore(before.score)} → removed` : 'Removed from your version'}
      </ChangePart>,
    );
    if (before?.material) parts.push(<Badge key="material">Was material</Badge>);
    return <>{parts}</>;
  }

  if (after.kind === 'not-projected') {
    parts.push(<ChangePart key="note">{NOT_PROJECTED_NOTE[after.reason ?? 'financial-effects']}</ChangePart>);
    return <>{parts}</>;
  }

  if (hasEditedScoredField(edits)) {
    parts.push(<ChangePart key="edited">Edited: the score is recalculated when you integrate</ChangePart>);
    return <>{parts}</>;
  }

  if (after.score !== undefined) {
    parts.push(
      <ChangePart key="score">
        {before?.score !== undefined
          ? `Score ${formatScore(before.score)} → ${formatScore(after.score)}`
          : `Score ${formatScore(after.score)}`}
      </ChangePart>,
    );
  }
  parts.push(<Badge key="material">{materialityOutcome(before?.material, after.material)}</Badge>);
  return <>{parts}</>;
}

const NOT_PROJECTED_NOTE: Record<OwnInsightNotProjectedReason, string> = {
  'financial-effects': 'Financial effects decide its materiality when integrated',
  'threshold-in-proposal': 'Scored against the threshold this proposal sets when integrated',
  'no-thresholds': 'Its materiality is known after the recalculation',
  'financial-v2': 'This assessment doesn’t apply own insights to risks and opportunities yet',
};

/** About the IRO in the row, never the matter: a matter's result is known only after recalculation. */
function materialityOutcome(before: boolean | undefined, after: boolean): string {
  if (before === undefined) return after ? 'Material' : 'Below the threshold';
  if (before && after) return 'Stays material';
  if (before) return 'Stops being material';
  if (after) return 'Becomes material';
  return 'Stays below the threshold';
}

/** Scores are shown as the platform's table shows them; the outcome was decided on the unrounded score. */
function formatScore(score: number): string {
  return String(Math.round(score));
}

function formatNumber(value: number): string {
  return String(Math.round(value * 100) / 100);
}

function proposedValue(item: ProposedItem, edits: ItemEdits | undefined, key: string): string | undefined {
  const edited = edits?.[key];
  if (typeof edited === 'string') return edited;
  const proposed = (item.tailoring as unknown as Record<string, unknown>)[key];
  return typeof proposed === 'string' ? proposed : undefined;
}

// ---------------------------------------------------------------------------
// Row details: the field-by-field comparison with the controls, the description, the reasoning, the
// evidence note and the conflict.
// ---------------------------------------------------------------------------

function RowDetails({
  item,
  row,
  edits,
  locked,
  onEditField,
}: {
  item: ProposedItem;
  row: OwnInsightRowData | undefined;
  edits: ItemEdits | undefined;
  locked: boolean;
  onEditField: (itemId: string, field: string, value: EditValue) => void;
}) {
  const editable = isEditableTailoring(item);
  const proposedTailoring = item.tailoring as unknown as Record<string, unknown>;
  const description =
    (edits?.description as string | undefined) ?? (proposedTailoring.description as string | undefined);
  const reasoning =
    (edits?.reasoning as string | undefined) ?? (proposedTailoring.reasoning as string | undefined) ?? item.reasoning;
  return (
    <>
      {item.tailoringType === 'patch-im' || item.tailoringType === 'patch-fm' ? (
        <FieldTable item={item} row={row} edits={edits} locked={locked} onEditField={onEditField} />
      ) : null}
      {item.tailoringType === 'patch-fe' && (
        <FinancialEffectFields item={item} edits={edits} locked={locked} onEditField={onEditField} />
      )}
      {(item.tailoringType === 'patch-fm' || item.tailoringType === 'patch-fe') && (
        <DriverFields item={item} edits={edits} locked={locked} onEditField={onEditField} />
      )}
      {editable && description !== undefined && (
        <TextArea
          label="Description"
          value={description}
          rows={2}
          isDisabled={locked}
          onChange={value => {
            onEditField(item.itemId, 'description', value);
          }}
        />
      )}
      <TextArea
        label="Reasoning, saved to the activity log"
        value={reasoning}
        rows={3}
        isDisabled={locked}
        onChange={value => {
          onEditField(item.itemId, 'reasoning', value);
        }}
      />
      {item.group === 'gaps' && item.evidenceGapNote && (
        <Stack gap="xs">
          <Text size="sm" height="xs" weight="semibold" color="gray-900">
            Why the evidence is limited
          </Text>
          <Text size="sm" height="xs" color="gray-800">
            {item.evidenceGapNote}
          </Text>
        </Stack>
      )}
      {item.conflict && (
        <Stack gap="xs">
          <Text size="sm" height="xs" weight="semibold" color="gray-900">
            Conflict
          </Text>
          <Text size="sm" height="xs" color="gray-800">
            {item.conflict.note}
          </Text>
          <Text size="sm" height="xs" color="gray-600">
            {RECOMMENDATION_LABEL[item.conflict.recommendation]}
          </Text>
        </Stack>
      )}
    </>
  );
}

/** Field | Upright | Proposed, one row per compared field, with the proposed value editable. */
function FieldTable({
  item,
  row,
  edits,
  locked,
  onEditField,
}: {
  item: ProposedItem;
  row: OwnInsightRowData | undefined;
  edits: ItemEdits | undefined;
  locked: boolean;
  onEditField: (itemId: string, field: string, value: EditValue) => void;
}) {
  const fields = comparedFields(item);
  const before = row?.before;
  const beforeLabel = before?.source === 'own-insight' ? 'Current' : 'Upright';
  return (
    <table className={styles.fieldTable}>
      <thead>
        <tr>
          <th>
            <Text size="sm" height="xs" weight="semibold" color="gray-600">
              Field
            </Text>
          </th>
          {before && (
            <th>
              <Text size="sm" height="xs" weight="semibold" color="gray-600">
                {beforeLabel}
              </Text>
            </th>
          )}
          <th>
            <Text size="sm" height="xs" weight="semibold" color="gray-600">
              Proposed
            </Text>
          </th>
        </tr>
      </thead>
      <tbody>
        {fields.map(field => {
          const value = proposedValue(item, edits, field.key) ?? '';
          return (
            <tr key={field.key}>
              <td>
                <Text size="sm" height="xs" color="gray-900">
                  {field.label}
                </Text>
              </td>
              {before && (
                <td>
                  <Text size="sm" height="xs" color="gray-800">
                    {fieldValueLabel(field, before.fields[field.key]) ?? '—'}
                  </Text>
                </td>
              )}
              <td>
                <Select
                  label={field.label}
                  isLabelVisuallyHidden
                  variant="outlined"
                  size="small"
                  value={value === '' ? null : value}
                  placeholder="Select…"
                  isDisabled={locked}
                  onChange={key => {
                    if (typeof key === 'string') onEditField(item.itemId, field.key, key);
                  }}
                >
                  {optionsWithCurrent(field, value).map(option => (
                    <Option key={option.value} id={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </td>
            </tr>
          );
        })}
        {row?.after.kind === 'scored' && row.after.score !== undefined && !hasEditedScoredField(edits) && (
          <tr>
            <td>
              <Text size="sm" height="xs" weight="semibold" color="gray-900">
                Materiality score
              </Text>
            </td>
            {before && (
              <td className={styles.numeric}>
                <Text size="sm" height="xs" color="gray-800">
                  {before.score !== undefined ? formatScore(before.score) : '—'}
                </Text>
              </td>
            )}
            <td className={styles.numeric}>
              <Text size="sm" height="xs" weight="semibold" color="gray-900">
                {`${formatScore(row.after.score)} · ${materialityOutcome(before?.material, row.after.material).toLowerCase()}`}
              </Text>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/** A financial-effect risk or opportunity keeps whichever form the agent proposed: a level or a percentage. */
function FinancialEffectFields({
  item,
  edits,
  locked,
  onEditField,
}: {
  item: ProposedItem;
  edits: ItemEdits | undefined;
  locked: boolean;
  onEditField: (itemId: string, field: string, value: EditValue) => void;
}) {
  const proposed = item.tailoring as unknown as Record<string, unknown>;
  const valueOf = (key: string): EditValue => edits?.[key] ?? (proposed[key] as EditValue);
  const levelOrValue = (levelKey: string, valueKey: string, label: string) =>
    valueKey in proposed ? (
      <PercentField
        key={valueKey}
        label={`${label} (%)`}
        value={valueOf(valueKey)}
        isDisabled={locked}
        onChange={value => {
          onEditField(item.itemId, valueKey, value);
        }}
      />
    ) : (
      <LevelSelect
        key={levelKey}
        label={label}
        options={FINANCIAL_LEVEL_OPTIONS}
        value={(valueOf(levelKey) as string | undefined) ?? ''}
        isDisabled={locked}
        onChange={value => {
          onEditField(item.itemId, levelKey, value);
        }}
      />
    );
  return (
    <Stack row gap="md" wrap>
      {levelOrValue('probabilityLevel', 'probabilityValue', 'Probability')}
      {levelOrValue('magnitudeLevel', 'magnitudeValue', 'Magnitude')}
      <LevelSelect
        label="Time horizon"
        options={TIME_HORIZON_OPTIONS}
        value={(valueOf('timeHorizon') as string | undefined) ?? ''}
        isDisabled={locked}
        onChange={value => {
          onEditField(item.itemId, 'timeHorizon', value);
        }}
      />
      <PercentField
        label="Share of financial item affected (%)"
        value={valueOf('ratioOfItemAffected')}
        isDisabled={locked}
        onChange={value => {
          onEditField(item.itemId, 'ratioOfItemAffected', value);
        }}
      />
    </Stack>
  );
}

/** Driver, then a financial item valid for it. Changing the driver resets an item it does not allow. */
function DriverFields({
  item,
  edits,
  locked,
  onEditField,
}: {
  item: ProposedItem;
  edits: ItemEdits | undefined;
  locked: boolean;
  onEditField: (itemId: string, field: string, value: EditValue) => void;
}) {
  const proposed = item.tailoring as unknown as Record<string, unknown>;
  const driver = (edits?.driver as string | undefined) ?? (proposed.driver as string | undefined) ?? '';
  const validItems =
    driver && driver in driverToValidFinancialItems ? driverToValidFinancialItems[driver as FinancialDriver] : [];
  const financialItem =
    (edits?.financialItem as string | undefined) ?? (proposed.financialItem as string | undefined) ?? '';
  return (
    <Stack row gap="md" wrap>
      <LevelSelect
        label="Driver"
        options={FINANCIAL_DRIVER_OPTIONS}
        value={driver}
        isDisabled={locked}
        onChange={value => {
          onEditField(item.itemId, 'driver', value);
          const nextValid =
            value in driverToValidFinancialItems ? driverToValidFinancialItems[value as FinancialDriver] : [];
          if (!nextValid.includes(financialItem as FinancialItem)) {
            onEditField(item.itemId, 'financialItem', nextValid[0]);
          }
        }}
      />
      <LevelSelect
        label="Financial item"
        options={validItems.map(financial => ({ value: financial, label: financial }))}
        value={financialItem}
        isDisabled={locked || !driver}
        onChange={value => {
          onEditField(item.itemId, 'financialItem', value);
        }}
      />
    </Stack>
  );
}

function LevelSelect({
  label,
  options,
  value,
  isDisabled,
  onChange,
}: {
  label: string;
  options: SelectOption[];
  value: string;
  isDisabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      label={label}
      variant="outlined"
      size="small"
      value={value === '' ? null : value}
      placeholder="Select…"
      isDisabled={isDisabled}
      onChange={key => {
        if (typeof key === 'string') onChange(key);
      }}
    >
      {options.map(option => (
        <Option key={option.value} id={option.value}>
          {option.label}
        </Option>
      ))}
    </Select>
  );
}

function PercentField({
  label,
  value,
  isDisabled,
  onChange,
}: {
  label: string;
  value: EditValue;
  isDisabled: boolean;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <Input
      label={label}
      type="number"
      value={ratioToPercentInput(value)}
      isDisabled={isDisabled}
      onChange={raw => {
        onChange(percentInputToRatio(raw));
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// The confirmation step: what integrating does, before it does it.
// ---------------------------------------------------------------------------

function CommitStep({
  selectedItems,
  rowsById,
  companyName,
  edits,
  onBack,
  onConfirm,
}: {
  selectedItems: ProposedItem[];
  rowsById: Map<string, OwnInsightRowData>;
  companyName: string | undefined;
  edits: Record<string, ItemEdits>;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const count = selectedItems.length;
  const crossings = countThresholdCrossings(selectedItems, rowsById, edits);
  const target = companyName ? `${companyName}’s tailored version` : 'the tailored version';
  return (
    <div className={styles.commit} role="group" aria-label="Confirm integration">
      <Text size="md" height="sm" weight="semibold" color="gray-900">
        {`Integrate ${insightsCountLabel(count)} into ${target}?`}
      </Text>
      <div className={styles.commitLines}>
        <Text size="sm" height="xs" color="gray-800">
          The tailored version is recalculated. This takes about a minute.
        </Text>
        {crossings !== undefined && (
          <Text size="sm" height="xs" color="gray-800">
            {crossings}
          </Text>
        )}
        <Text size="sm" height="xs" color="gray-800">
          Upright’s own results don’t change. They stay one switch away, under Model: Upright.
        </Text>
        <Text size="sm" height="xs" color="gray-800">
          Everyone in your organization who can open this company sees the tailored version.
        </Text>
        <Text size="sm" height="xs" color="gray-800">
          You can undo these changes later, here in the chat or from the activity log.
        </Text>
      </div>
      <Stack row gap="sm" align="center" wrap>
        <Button variant="outlined" size="small" onPress={onBack}>
          Back
        </Button>
        <Button variant="contained" size="small" onPress={onConfirm}>
          {`Integrate ${insightsCountLabel(count)}`}
        </Button>
      </Stack>
    </div>
  );
}

/**
 * "1 of them becomes material, and 1 new one is material", from the rows' own projections. A new
 * impact, risk or opportunity has no Upright value to cross from, so it is counted apart.
 */
function countThresholdCrossings(
  items: ProposedItem[],
  rowsById: Map<string, OwnInsightRowData>,
  edits: Record<string, ItemEdits>,
): string | undefined {
  let becomes = 0;
  let stops = 0;
  let added = 0;
  for (const item of items) {
    const row = rowsById.get(item.itemId);
    if (!row) continue;
    const isProjected = row.after.kind === 'scored' && !hasEditedScoredField(edits[item.itemId]);
    if (isNewIro(item)) {
      if (isProjected && row.after.kind === 'scored' && row.after.material) added += 1;
      continue;
    }
    // An edit whose IRO was not found in the results has nothing to cross from.
    if (!row.before) continue;
    if (row.after.kind === 'removed' && row.before.material) stops += 1;
    if (!isProjected || row.after.kind !== 'scored') continue;
    if (!row.before.material && row.after.material) becomes += 1;
    if (row.before.material && !row.after.material) stops += 1;
  }
  const parts = [
    becomes > 0 ? `${becomes} of them ${becomes === 1 ? 'becomes' : 'become'} material` : undefined,
    stops > 0 ? `${stops} ${stops === 1 ? 'stops' : 'stop'} counting as material` : undefined,
    added > 0 ? `${added} new ${added === 1 ? 'one is' : 'ones are'} material` : undefined,
  ].filter((part): part is string => part !== undefined);
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return `${parts.join('')}.`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts.slice(-1).join('')}.`;
}

// ---------------------------------------------------------------------------
// The receipt: what was integrated, the recalculation, and what it changed.
// ---------------------------------------------------------------------------

function TailoringBatchReceipt({
  result,
  items,
  rowsById,
  context,
  priorDmaLabel,
}: {
  result: BatchApplyCsrdResultTailoringResponse;
  items: ProposedItem[];
  rowsById: Map<string, OwnInsightRowData>;
  context: OwnInsightProposalContext | undefined;
  priorDmaLabel: string | undefined;
}) {
  const runtime = useAssistantRuntime();
  const isThreadRunning = useThread(state => state.isRunning);
  const cardRef = useRef<HTMLDivElement>(null);
  const [undoAsked, setUndoAsked] = useState(false);

  const skippedSet = new Set(result.skippedItemIds);
  // PLT-7740: why an item was skipped; absent on results stored before the reason was recorded.
  const skipMessageById = new Map((result.skippedItems ?? []).map(skipped => [skipped.itemId, skipped.message]));
  // PLT-7740: integrated items that updated the IRO's existing tailoring instead of adding one.
  const updatedSet = new Set(result.updatedItemIds ?? []);
  // PLT-7535: supersede items the server refused because the IRO had already been edited.
  const rejectionById = new Map((result.rejectedItems ?? []).map(rejected => [rejected.itemId, rejected]));
  const integrated = items.filter(item => !skippedSet.has(item.itemId) && !rejectionById.has(item.itemId));
  const skipped = items.filter(item => skippedSet.has(item.itemId));
  const rejected = items.filter(item => rejectionById.has(item.itemId));

  const integratedMatterIds = new Set<string>(
    integrated.flatMap(item => ('matterId' in item.tailoring ? [item.tailoring.matterId] : [])),
  );
  const recalculation = useRecalculation(
    result,
    (context?.matters ?? []).filter(matter => integratedMatterIds.has(matter.matterId)),
  );

  const title = result.alreadyApplied
    ? 'Own insights already integrated'
    : integrated.length === 0
      ? 'No own insights integrated'
      : `${insightsCountLabel(integrated.length)} integrated`;

  const askUndo = () => {
    setUndoAsked(true);
    cardRef.current?.closest('.aui-thread-viewport')?.dispatchEvent(new Event(FOLLOW_THREAD_EVENT));
    const headlines = integrated.map(item => `- ${rowHeadline(item, rowsById.get(item.itemId))}`).join('\n');
    runtime.thread.append(
      `Undo the own insights I just integrated${context?.companyName ? ` for ${context.companyName}` : ''}:\n${headlines}`,
    );
  };

  const dmaPath = context?.tailoredDmaUrl
    ? (sameOriginPlatformPath(context.tailoredDmaUrl) ?? context.tailoredDmaUrl)
    : undefined;

  return (
    <div ref={cardRef}>
      <DataCard>
        <Stack gap="lg">
          <HeadingGroup>
            <Heading level={3} size="lg" height="lg" weight="bold" color="gray-900">
              {title}
            </Heading>
            <Paragraph size="sm" height="xs" color="gray-600">
              {joinSubtitle([
                context?.companyName,
                skipped.length > 0 ? `${skipped.length} skipped` : undefined,
                rejected.length > 0 ? `${rejected.length} not applied` : undefined,
                priorDmaLabel ? `Carried over from ${priorDmaLabel}` : undefined,
              ])}
            </Paragraph>
          </HeadingGroup>
          {result.recomputeJobId !== undefined && (
            <RecalculationStatus
              recalculation={recalculation}
              thresholdChanged={integrated.some(isThresholdTailoring)}
            />
          )}
          {integrated.length > 0 && (
            <ReceiptList
              items={integrated}
              rowsById={rowsById}
              note={item =>
                updatedSet.has(item.itemId)
                  ? 'Changed the values of the existing own insight instead of adding one.'
                  : undefined
              }
            />
          )}
          {skipped.length > 0 && (
            <Stack gap="sm">
              <Text size="sm" height="xs" weight="semibold" color="gray-900">
                Skipped
              </Text>
              <ReceiptList
                items={skipped}
                rowsById={rowsById}
                note={item => skipMessageById.get(item.itemId) ?? 'An equivalent own insight already exists.'}
              />
            </Stack>
          )}
          {rejected.length > 0 && (
            <Stack gap="sm">
              <Text size="sm" height="xs" weight="semibold" color="gray-900">
                Not applied
              </Text>
              <ReceiptList
                items={rejected}
                rowsById={rowsById}
                note={item => rejectionById.get(item.itemId)?.message}
              />
            </Stack>
          )}
          {(integrated.length > 0 || dmaPath) && (
            <Stack row gap="sm" align="center" wrap>
              {integrated.length > 0 && (
                <Button variant="outlined" size="small" isDisabled={undoAsked || isThreadRunning} onPress={askUndo}>
                  Undo these changes
                </Button>
              )}
              {dmaPath && (
                <LinkButton variant="contained" size="small" href={dmaPath}>
                  Open tailored DMA
                </LinkButton>
              )}
            </Stack>
          )}
        </Stack>
      </DataCard>
    </div>
  );
}

function ReceiptList({
  items,
  rowsById,
  note,
}: {
  items: ProposedItem[];
  rowsById: Map<string, OwnInsightRowData>;
  note: (item: ProposedItem) => string | undefined;
}) {
  return (
    <OwnInsightRows>
      {items.map(item => {
        const row = rowsById.get(item.itemId);
        return (
          <OwnInsightRow
            key={item.itemId}
            headline={rowHeadline(item, row)}
            meta={matterLine(row?.matterName ?? item.matterName, note(item))}
          />
        );
      })}
    </OwnInsightRows>
  );
}

/** Matter names by how their materiality compares before and after the recalculation. */
type MatterOutcomes = {
  nowMaterial: string[];
  noLongerMaterial: string[];
  stillMaterial: string[];
  stillNotMaterial: string[];
};

type Recalculation =
  | { state: 'running' }
  | { state: 'failed' }
  | { state: 'done'; outcomes: MatterOutcomes | undefined };

/**
 * Follows the queued recompute job (its id comes back from the apply, and from an `alreadyApplied`
 * replay), refreshes the open DMA views once it succeeds, and then reads the recalculated results of
 * the matters the proposal's items are in. Only those matters have a before-state, so the receipt
 * speaks about them alone; a threshold change can move any other matter too.
 */
function useRecalculation(result: BatchApplyCsrdResultTailoringResponse, matters: OwnInsightMatter[]): Recalculation {
  const queryClient = useQueryClient();
  const statusQuery = useFetchJobStatus(result.recomputeJobId);
  const status = statusQuery.data?.status;
  const succeeded = status === JobStatus.SUCCESS;

  const hasInvalidated = useRef(false);
  useEffect(() => {
    if (!succeeded || hasInvalidated.current) return;
    hasInvalidated.current = true;
    void queryClient.invalidateQueries({ queryKey: ['materiality'] });
  }, [succeeded, queryClient]);

  // Read once and kept: the receipt records what this recalculation changed. Outside the
  // 'materiality' keys on purpose, so a later undo or proposal that refreshes them does not rewrite it.
  const resultsQuery = useQuery({
    queryKey: ['own-insight-receipt', result.tailoredVariantId, result.recomputeJobId],
    queryFn: async () => await fetchMaterialityForVariant({ variantId: result.tailoredVariantId }, {}),
    enabled: succeeded && matters.length > 0,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (status === JobStatus.ERROR) return { state: 'failed' };
  if (result.recomputeJobId === undefined || !statusQuery.data || !getIsJobComplete(statusQuery.data.status)) {
    return { state: 'running' };
  }
  if (!resultsQuery.data) return { state: 'done', outcomes: undefined };

  const afterById = new Map(
    resultsQuery.data.map(matter => [
      matter.matterId as string,
      matter.imResult === CompanyLevelMaterialityResult.MATERIAL ||
        matter.fmResult === CompanyLevelMaterialityResult.MATERIAL,
    ]),
  );
  const outcomes: MatterOutcomes = { nowMaterial: [], noLongerMaterial: [], stillMaterial: [], stillNotMaterial: [] };
  for (const matter of matters) {
    const after = afterById.get(matter.matterId);
    if (after === undefined) continue;
    if (after) (matter.materialBefore ? outcomes.stillMaterial : outcomes.nowMaterial).push(matter.matterName);
    else (matter.materialBefore ? outcomes.noLongerMaterial : outcomes.stillNotMaterial).push(matter.matterName);
  }
  return { state: 'done', outcomes };
}

function RecalculationStatus({
  recalculation,
  thresholdChanged,
}: {
  recalculation: Recalculation;
  thresholdChanged: boolean;
}) {
  if (recalculation.state === 'running') {
    return (
      <div className={styles.status}>
        <Spinner size="small" />
        <Text size="sm" height="xs" color="gray-800">
          Recalculating the tailored version. This takes about a minute; open DMA views update by themselves.
        </Text>
      </div>
    );
  }
  if (recalculation.state === 'failed') {
    return (
      <div role="alert">
        <Text size="sm" height="xs" color="gray-800">
          The own insights were saved, but recalculating the tailored version failed, so its results may not show them
          yet. Changing any own insight retries the recalculation.
        </Text>
      </div>
    );
  }
  return (
    <Text size="sm" height="xs" color="gray-800">
      {recalculationSummary(recalculation.outcomes, thresholdChanged)}
    </Text>
  );
}

/**
 * "Health and safety is now material; Water is still not material." Names only the matters the
 * items are in: a matter no item is in has no before-state on the card to compare with.
 */
function recalculationSummary(outcomes: MatterOutcomes | undefined, thresholdChanged: boolean): string {
  const sentences = ['Tailored version recalculated.'];
  if (outcomes) {
    const parts = [
      matterClause(outcomes.nowMaterial, 'now material'),
      matterClause(outcomes.noLongerMaterial, 'no longer material'),
      matterClause(outcomes.stillMaterial, 'still material'),
      matterClause(outcomes.stillNotMaterial, 'still not material'),
    ].filter((part): part is string => part !== undefined);
    if (parts.length > 0) sentences.push(`${parts.join('; ')}.`);
  }
  if (thresholdChanged) {
    sentences.push('The new threshold applies to every matter, so open the tailored DMA to see them all.');
  }
  return sentences.join(' ');
}

function matterClause(names: string[], state: string): string | undefined {
  if (names.length === 0) return undefined;
  return `${listOf(names)} ${names.length === 1 ? 'is' : 'are'} ${state}`;
}

function listOf(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// ---------------------------------------------------------------------------
// Helpers.
// ---------------------------------------------------------------------------

function groupItems(items: ProposedItem[]): Record<Group, ProposedItem[]> {
  const grouped: Record<Group, ProposedItem[]> = {
    'user-request': [],
    'strong-evidence': [],
    gaps: [],
    'prior-dma': [],
  };
  for (const item of items) grouped[item.group].push(item);
  return grouped;
}
